import { Link } from '@tanstack/react-router';
import type { ServiceListItemDto } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Input } from '@upme/ui/components/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import { useMemo, useState } from 'react';
import { EmptyState } from '../../../shared/components/empty-state';

interface Props {
  services: ReadonlyArray<ServiceListItemDto>;
}

const STATUS_BADGE = {
  PUBLISHED: { label: 'опубликована', variant: 'default' as const },
  DRAFT: { label: 'черновик', variant: 'outline' as const },
  ARCHIVED: { label: 'в архиве', variant: 'secondary' as const },
  PENDING_MODERATION: { label: 'на модерации', variant: 'outline' as const },
};

export function ServicesTable({ services }: Props) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return services;
    return services.filter(
      (s) =>
        s.title.toLowerCase().includes(term) ||
        s.provider?.username?.toLowerCase().includes(term) ||
        s.categories.some((c) => c.name.toLowerCase().includes(term)),
    );
  }, [services, search]);

  if (services.length === 0) {
    return (
      <EmptyState
        title="Услуг пока нет"
        description="Услуги создаются исполнителями в мини-аппе. Здесь их можно модерировать и редактировать."
      />
    );
  }

  return (
    <div className="space-y-4">
      <Input
        placeholder="Поиск по названию, исполнителю или категории"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Название</TableHead>
            <TableHead>Исполнитель</TableHead>
            <TableHead>Категории</TableHead>
            <TableHead>Статус</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((s) => {
            const badge = STATUS_BADGE[s.status as keyof typeof STATUS_BADGE] ?? STATUS_BADGE.DRAFT;
            return (
              <TableRow key={s.id} className="cursor-pointer">
                <TableCell>
                  <Link to="/services/$serviceId" params={{ serviceId: s.id }} className="hover:underline">
                    {s.title}
                  </Link>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {s.provider?.username ? `@${s.provider.username}` : (s.provider?.firstName ?? '—')}
                </TableCell>
                <TableCell className="text-sm">
                  {s.categories.length > 0
                    ? s.categories.map((c) => [c.icon, c.name].filter(Boolean).join(' ')).join(' · ')
                    : '—'}
                </TableCell>
                <TableCell>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
